import { findParentNode } from "@tiptap/core";
import { Fragment, Node as ProseMirrorNode } from "@tiptap/pm/model";
import { Selection } from "@tiptap/pm/state";
import { cellAround, TableMap } from "@tiptap/pm/tables";

const TABLE_NODE_NAME = "table";
const TABLE_HEADER_NODE_NAME = "tableHeader";
const TABLE_CELL_NODE_NAME = "tableCell";

export interface TableContext {
    table: ProseMirrorNode;
    tablePos: number;
    tableStart: number;
    map: TableMap;
    rowIndex: number;
    columnIndex: number;
}

const findTable = findParentNode((node) => node.type.name === TABLE_NODE_NAME);

export function getTableContext(selection: Selection): TableContext | null {
    const table = findTable(selection);
    if (!table) {
        return null;
    }
    const cell = cellAround(selection.$anchor);
    if (!cell) {
        return null;
    }
    const tableStart = table.start;
    const map = TableMap.get(table.node);
    const rect = map.findCell(cell.pos - tableStart);
    return { table: table.node, tablePos: table.pos, tableStart, map, rowIndex: rect.top, columnIndex: rect.left };
}

export function hasMergedCells(table: ProseMirrorNode): boolean {
    let merged = false;
    table.descendants((node) => {
        if (merged) {
            return false;
        }
        if (node.type.name === TABLE_CELL_NODE_NAME || node.type.name === TABLE_HEADER_NODE_NAME) {
            merged = (node.attrs.colspan ?? 1) > 1 || (node.attrs.rowspan ?? 1) > 1;
            return false;
        }
        return true;
    });
    return merged;
}

export function isHeaderRow(row: ProseMirrorNode): boolean {
    if (row.childCount === 0) {
        return false;
    }
    return children(row).every((cell) => cell.type.name === TABLE_HEADER_NODE_NAME);
}

export function reorderTableRows(table: ProseMirrorNode, from: number, to: number): ProseMirrorNode | null {
    if (hasMergedCells(table) || !isValidMove(table.childCount, from, to)) {
        return null;
    }
    const rows = children(table);
    if (isHeaderRow(rows[from]) || isHeaderRow(rows[to])) {
        return null;
    }
    return table.copy(Fragment.from(moveItem(rows, from, to)));
}

export function reorderTableColumns(table: ProseMirrorNode, from: number, to: number): ProseMirrorNode | null {
    if (hasMergedCells(table)) {
        return null;
    }
    const rows = children(table);
    const columnCount = rows[0]?.childCount ?? 0;
    if (!isValidMove(columnCount, from, to) || rows.some((row) => row.childCount !== columnCount)) {
        return null;
    }
    const reorderedRows = rows.map((row) => row.copy(Fragment.from(moveItem(children(row), from, to))));
    return table.copy(Fragment.from(reorderedRows));
}

function children(node: ProseMirrorNode): ProseMirrorNode[] {
    const result: ProseMirrorNode[] = [];
    node.forEach((child) => {
        result.push(child);
    });
    return result;
}

function isValidMove(count: number, from: number, to: number): boolean {
    return from !== to && from >= 0 && to >= 0 && from < count && to < count;
}

function moveItem<T>(items: T[], from: number, to: number): T[] {
    const result = [...items];
    const [item] = result.splice(from, 1);
    result.splice(to, 0, item);
    return result;
}
